import { useCallback, useEffect, useMemo, useState } from 'react'
import { useBoolean } from 'react-use'
import { v4 as uuid4 } from 'uuid'

import { AppBuilderAPI } from '@makyoapp/makyo-sdk'
import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { IBody } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { BuilderPage } from '@makyoapp/makyo-common'

import { IAppPage } from '@/core/app-builder/app/page'
import constate from '@/lib/constate'
import { isDescendant } from '@/lib/slate'
import { toSdkPage, toSdkPageList } from '@/adapters/__project__/BuilderPageAdapter'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { checkVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useReqError } from '@/hooks/useReqError'
import { STORAGE_KEYS } from '@/shared/constants'
import { createSlug } from '@/shared/utils'
import { genNewPage } from '@/utils/builder/app-builder'

import { useAppRoutingContext } from './__project__/AppRoutingContext'

const [AppPagesProvider, useAppPagesContext] = constate('AppPagesContext', () => {
  // Access Global Contexts
  const { tables, dbClient, language, userSpecific } = useDbClientContext()
  const { appClient, appID, pages, setPages, updateVersion } = useAppBuilderContext()
  const { goToPage, currentSlug, currentRowId, locationState } = useAppRoutingContext()
  const { isAuthenticated, postUnauthenticatedPageSlug } = useAppAuthContext()
  const { userEmail } = useAppAuthContext()

  const printAPIError = useReqError()

  // Local State
  const [isPageLoading, setIsPageLoading] = useBoolean(false)
  const [previewedPageIds, setPreviewedPageIds] = useState<string[]>([])

  // Active Page State
  const [activePageIndex, activePage, activePageClient] = useMemo(() => {
    let pageIndex = -1
    let activePage: BuilderPage | undefined = undefined
    let activePageClient: IAppPage | undefined = undefined

    if (currentSlug && !currentRowId) {
      const isFormAdd = currentSlug.endsWith('-form-add')

      if (isFormAdd) {
        let tableId = ''

        if (locationState && locationState.tablePageId) {
          tableId = locationState.tablePageId
        } else {
          const tableName = currentSlug.replace('-form-add', '')
          const guessedTable = tables.find((tb) => createSlug(tb.name.toLowerCase()) === tableName)
          if (guessedTable) {
            tableId = guessedTable.id
          }
        }

        pageIndex = pages.findIndex(
          (p) => p.tableId === tableId && p?.nature === 'form' && p.title.startsWith('form-add')
        )
      } else {
        // access from page slug
        pageIndex = pages.findIndex((p) => p.slug === currentSlug)
      }

      //
    } else if (currentRowId && currentSlug) {
      // find related page with current tableId
      const isFormPage = currentSlug.endsWith('-form')
      const baseSlug = isFormPage ? currentSlug.replace('-form', '') : currentSlug

      if (locationState && locationState.tablePageId) {
        // access from table page with rowId, but still check if it's a form
        pageIndex = pages.findIndex(
          (p) => p.tableId === locationState.tablePageId && (isFormPage ? p?.nature === 'form' : !p?.nature)
        )
      } else {
        let currentDetailTableId = tables.find((tb) => encodeURIComponent(tb.name.toLowerCase()) === baseSlug)?.id
        pageIndex = pages.findIndex(
          (p) => p.tableId === currentDetailTableId && (isFormPage ? p?.nature === 'form' : !p.nature)
        )
      }
    }

    if (pageIndex > -1) {
      activePage = pages[pageIndex]
      activePageClient = appClient.pagesMap.get(activePage?.id)
    }

    return [pageIndex, activePage, activePageClient]
    //
  }, [appClient?.pagesMap, currentRowId, currentSlug, locationState, pages, tables])

  /********************************************************
   * EFFECTS
   ********************************************************/

  // if no page found, redirect to existing page
  useEffect(() => {
    if (activePageIndex === -1 && pages.length > 0) {
      goToPage({ slug: pages[0].slug })
    }
  }, [activePageIndex, goToPage, pages])

  const pagesNotEmpty = !!activePage && pages.length > 0

  // if user is not logged in and the page is private, redirect to the unauthenticated page
  useEffect(() => {
    const isPrivatePage = !!activePage && activePage.private && !isAuthenticated && !!postUnauthenticatedPageSlug

    if (pagesNotEmpty && isPrivatePage) {
      goToPage({ slug: postUnauthenticatedPageSlug })
    }
  }, [activePage, goToPage, isAuthenticated, pagesNotEmpty, postUnauthenticatedPageSlug])

  /********************************************************
   * METHODS
   ********************************************************/

  const addPage = useCallback(
    async (tableId?: string) => {
      const tableName = tableId ? tables.find((tb) => tb.id === tableId)?.name : undefined

      const newPage: BuilderPage = genNewPage(pages, tableId, tableName)

      setIsPageLoading(true)

      try {
        const page = await appClient.AddPage(toSdkPage(newPage))
        goToPage({ slug: page._Slug })
      } catch (e) {
        printAPIError(e as ErrorResponse)
      } finally {
        setIsPageLoading(false)
      }

      updateVersion()

      return newPage
    },
    [tables, pages, setIsPageLoading, appClient, goToPage, printAPIError, updateVersion]
  )

  const deletePage = useCallback(
    async (id: string, updateVer = true) => {
      if (!pages) return

      const nextPages = pages.filter((page) => page.id !== id)
      setPages(nextPages)

      if (activePage?.id === id) {
        goToPage({ slug: nextPages[0]?.slug })
      }
      try {
        await appClient.DeletePage(id)
      } catch (e) {
        printAPIError(e as ErrorResponse)
      }
      updateVersion()
    },
    [activePage?.id, appClient, goToPage, pages, printAPIError, setPages, updateVersion]
  )

  const clonePage = useCallback(
    async (page: BuilderPage) => {
      // check if current page title isDescendant
      const newTitle = isDescendant(page.title) ? 'screen' : `${page.title} copy`
      const newSlug = createSlug(newTitle)

      const clonedPage: BuilderPage = {
        ...page,
        id: uuid4(),
        title: newTitle,
        slug: newSlug,
      }

      setIsPageLoading(true)

      try {
        const page = await appClient.AddPage(toSdkPage(clonedPage))
        goToPage({ slug: page._Slug })
      } catch (e) {
        printAPIError(e as ErrorResponse)
      } finally {
        setIsPageLoading(false)
      }

      updateVersion()

      return clonedPage
    },
    [setIsPageLoading, appClient, goToPage, printAPIError, updateVersion]
  )

  const reorderPage = useCallback(
    async (cb: (prevPages: BuilderPage[]) => BuilderPage[]) => {
      if (!pages) return

      const nextPages = [...pages]
      const reorderedPages = cb(nextPages)

      try {
        // Update the local state immediately
        setPages(reorderedPages)

        // Convert pages to SDK format for backend update
        const sdkPages = toSdkPageList(reorderedPages)

        appClient.ReorderPage(sdkPages)
      } catch (error) {
        printAPIError(error as ErrorResponse)
      }
    },
    [appClient, pages, printAPIError, setPages]
  )

  const disablePage = useCallback(
    async (enabled: boolean, pageID: string, updateVer = false) => {
      if (!pages) return
      try {
        const currentPageClient = appClient.pagesMap.get(pageID)
        if (currentPageClient) {
          currentPageClient.Disable(!enabled)
        }
      } catch (error) {
        printAPIError(error as ErrorResponse)
      }
      await updateVersion()
    },
    [appClient.pagesMap, pages, printAPIError, updateVersion]
  )

  const makePrivate = useCallback(
    async (isPrivate: boolean, pageID: string, updateVer = false) => {
      if (!pages) return
      try {
        const currentPageClient = appClient.pagesMap.get(pageID)
        if (currentPageClient) {
          currentPageClient.MakePrivate(!isPrivate)
        }
      } catch (error) {
        printAPIError(error as ErrorResponse)
      }
      await updateVersion()
    },
    [appClient.pagesMap, pages, printAPIError, updateVersion]
  )

  const toggleVisibility = useCallback(
    async (isVisible: boolean, pageID: string, updateVer = false) => {
      if (!pages) return
      try {
        const currentPageClient = appClient.pagesMap.get(pageID)
        if (currentPageClient) {
          currentPageClient.ToggleVisibility(!isVisible)
        }
      } catch (error) {
        printAPIError(error as ErrorResponse)
      }
      await updateVersion()
    },
    [appClient, updateVersion, pages, printAPIError]
  )

  const updatePageAlt = useCallback(
    async (idx: number, cb: (prev: BuilderPage) => void) => {
      const copy = [...pages]
      cb(copy[idx])

      // setPages(copy)

      const page = toSdkPage(copy[idx])

      try {
        const currentPageClient = appClient.pagesMap.get(page._id)
        if (currentPageClient) {
          await currentPageClient.Update(page)
        }
      } catch (e) {
        printAPIError(e as ErrorResponse)
      }
    },
    [pages, appClient.pagesMap, printAPIError]
  )

  const updateSlug = useCallback(
    async (pageId: string, pageIdx: number, slug: string) => {
      try {
        await AppBuilderAPI.ReqUpdatePageSlug(appID, pageId, slug)

        //
      } catch (error) {
        printAPIError(error as ErrorResponse)
      }
    },
    [appID, printAPIError]
  )

  /********************************************************
   * COMPONENT
   ********************************************************/

  // this is to check if the component is child of form because on the navigator, child of form should not be able to delete or to clone
  const isChildOfForm = (targetId: string): boolean => {
    if (!activePage?.id || !activePageClient) return false

    const checkInBodyArray = (bodyArray: IBody[]): boolean => {
      for (const component of bodyArray) {
        if (component._ComponentID === 'form') {
          // Find the last button in the form
          const lastButton = component._Body?.reverse().find((child) => child._ComponentID === 'button')

          // If the last button exists and is the target, and it's the only button, prevent deletion
          if (
            lastButton &&
            lastButton._id === targetId &&
            component._Body?.filter((child) => child._ComponentID === 'button').length === 1
          ) {
            return true
          }
        }

        // Recurse through nested components
        if (component._Body?.length > 0 && checkInBodyArray(component._Body)) {
          return true // Early exit if found in nested body
        }
      }
      return false
    }

    // Start checking from the top-level body
    return checkInBodyArray(activePageClient.settings._Body || [])
  }

  /********************************************************
   * NAVIGATION
   ********************************************************/

  /**
   * Navigate to a page safely
   * This is to check if the user has right to navigate to the page
   * If the page is private and the user is not logged in, it will redirect to the unauthenticated page
   * If the page is not visible to the user based on the visibility settings, it will redirect to the default page
   * If the page is visible to the user, it will navigate to the page
   */
  const goToPageSafely = useCallback(
    (slugOrId: string, replace = false) => {
      setPreviewedPageIds([])

      const userHasRightToNavigate = checkVisibilityV2(
        activePage?.visibility,
        dbClient,
        userEmail,
        language,
        userSpecific
      )
      const defaultRedirectPage = postUnauthenticatedPageSlug ?? pages[0]?.slug
      const desiredPage = pages.find((page) => page.slug === slugOrId || page.id === slugOrId)

      // Check if the page is in previewed pages - if so, allow navigation
      const isPagePreviewed = desiredPage?.id && previewedPageIds.includes(desiredPage.id)

      // Add this line to check if current page is being previewed
      const isCurrentPagePreviewed = activePage?.id && previewedPageIds.includes(activePage.id)

      if (desiredPage?.private) {
        const isLoggedIn = !!localStorage.getItem(`${STORAGE_KEYS.APP_USER_AUTH}:${window.appID}`)
        if (!isLoggedIn && !isPagePreviewed) {
          return goToPage({ slug: defaultRedirectPage, redirect: slugOrId, replace })
        }
      }

      if (!userHasRightToNavigate && !isPagePreviewed && !isCurrentPagePreviewed) {
        return goToPage({ slug: defaultRedirectPage, replace })
      }

      goToPage({ slug: desiredPage?.slug, replace })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activePage?.visibility,
      dbClient,
      userEmail,
      language,
      userSpecific,
      postUnauthenticatedPageSlug,
      pages,
      previewedPageIds,
      goToPage,
    ]
  )

  return {
    pages,
    setPages,
    activePage,
    activePageClient,
    activePageIndex,
    addPage,
    deletePage,
    isPageLoading,
    clonePage,
    reorderPage,
    disablePage,
    toggleVisibility,
    updatePageAlt,
    updateSlug,
    makePrivate,

    // components
    isChildOfForm,

    // navigation
    goToPageSafely,

    // preview state
    previewedPageIds,
    setPreviewedPageIds,
  }
})

export { AppPagesProvider, useAppPagesContext }
